import React, {useRef, useState} from "react";
import {
    AlertCircle,
    CheckCircle2,
    Gamepad2,
    Keyboard,
    Play,
    RefreshCw,
    Timer,
    Trophy,
    UserIcon,
    XCircle
} from "lucide-react";
import api from "../api/axios";
import {useAuth} from "../features/auth/AuthContext";

const TypingGame = () => {
    // === 상태 관리 ===
    const [gameState, setGameState] = useState<'MENU' | 'PLAYING' | 'RESULT'>('MENU');
    const [level, setLevel] = useState(5); // 기본 N5
    const { user } = useAuth();

    // 게임 데이터
    interface Word {
        id: number;
        kanji: string;
        hiragana: string;
        meaning: string;
    }

    const [words, setWords] = useState<Word[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [input, setInput] = useState('');

    // 결과 관리
    interface GameHistory {
        word: Word;
        userAnswer: string;
        isCorrect: boolean;
    }

    const [history, setHistory] = useState<GameHistory[]>([]);
    const [score, setScore] = useState(0);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(0);

    // 랭킹 데이터
    interface Ranking {
        rank: number;
        username: string;
        score: number;
        elapsedTime: number;
    }

    const [rankings, setRankings] = useState<Ranking[]>([]);
    const [loading, setLoading] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const getThemeColors = (lvl: number) => {
        switch (lvl) {
            case 1: // N1: Red
                return {
                    text: 'text-red-500',
                    bg: 'bg-red-500',
                    bgLight: 'bg-red-50 dark:bg-red-900/20',
                    border: 'border-red-200 dark:border-red-900/50',
                    shadow: 'shadow-red-200 dark:shadow-red-900/10',
                    hoverBg: 'hover:bg-red-600',
                    ring: 'ring-red-500' // 버튼 포커스링용
                };
            case 2: // N2: Orange
                return {
                    text: 'text-orange-500',
                    bg: 'bg-orange-500',
                    bgLight: 'bg-orange-50 dark:bg-orange-900/20',
                    border: 'border-orange-200 dark:border-orange-900/50',
                    shadow: 'shadow-orange-200 dark:shadow-orange-900/10',
                    hoverBg: 'hover:bg-orange-600',
                    ring: 'ring-orange-500'
                };
            case 3: // N3: Yellow (가독성을 위해 text/bg는 조금 어둡게 조정 가능)
                return {
                    text: 'text-yellow-600 dark:text-yellow-400',
                    bg: 'bg-yellow-400',
                    bgLight: 'bg-yellow-50 dark:bg-yellow-900/20',
                    border: 'border-yellow-200 dark:border-yellow-900/50',
                    shadow: 'shadow-yellow-200 dark:shadow-yellow-900/10',
                    hoverBg: 'hover:bg-yellow-500',
                    ring: 'ring-yellow-400'
                };
            case 4: // N4: Green
                return {
                    text: 'text-green-500',
                    bg: 'bg-green-500',
                    bgLight: 'bg-green-50 dark:bg-green-900/20',
                    border: 'border-green-200 dark:border-green-900/50',
                    shadow: 'shadow-green-200 dark:shadow-green-900/10',
                    hoverBg: 'hover:bg-green-600',
                    ring: 'ring-green-500'
                };
            default: // N5: Blue
                return {
                    text: 'text-blue-500',
                    bg: 'bg-blue-500',
                    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
                    border: 'border-blue-200 dark:border-blue-900/50',
                    shadow: 'shadow-blue-200 dark:shadow-blue-900/10',
                    hoverBg: 'hover:bg-blue-600',
                    ring: 'ring-blue-500'
                };
        }
    };

    const currentTheme = getThemeColors(level);

    // === 게임 로직 ===

    // 1. 게임 시작
    const startGame = async () => {

        setLoading(true);
        try {
            // 백엔드에서 단어 10개 가져오기
            const response = await api.get(`/api/typing/words?level=${level}`);
            setWords(response.data);

            // 상태 초기화
            setCurrentIndex(0);
            setScore(0);
            setHistory([]);
            setInput('');
            setGameState('PLAYING');
            setStartTime(Date.now()); // 타이머 시작

            // 입력창 포커스 (약간의 지연 후)
            setTimeout(() => inputRef.current?.focus(), 100);
        } catch (error) {
            console.error("게임 시작 실패:", error);
            alert("게임을 시작할 수 없습니다. 서버 연결을 확인해주세요.");
        } finally {
            setLoading(false);
        }
    };

    // 2. 정답 제출 처리
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const currentWord = words[currentIndex];
        // 공백 제거 후 비교
        const isCorrect = input.trim() === currentWord.hiragana;

        // 기록 저장
        const newHistory = [...history, {
            word: currentWord,
            userAnswer: input,
            isCorrect
        }];
        setHistory(newHistory);

        if (isCorrect) setScore(prev => prev + 1);

        // 다음 문제로 이동 또는 종료
        if (currentIndex < words.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setInput('');
        } else {
            finishGame(newHistory, isCorrect ? score + 1 : score);
        }
    };

    // 3. 게임 종료 처리
    const finishGame = async (finalHistory: GameHistory[], finalScore: number) => {
        const finishTime = Date.now();
        setEndTime(finishTime);
        const elapsedTime = Math.floor((finishTime - startTime) / 1000); // 초 단위
        const username = user ? user.name : "Guest";
        setGameState('RESULT');

        try {
            // 3-1. 결과 서버 전송
            if (user) {
                await api.post('/api/typing/result', {
                    username,
                    level,
                    score: finalScore,
                    elapsedTime
                });
            }
            // 3-2. 최신 랭킹 조회
            const response = await api.get(`/api/typing/ranking?level=${level}`);
            setRankings(response.data);
        } catch (error) {
            console.error("결과 저장 실패:", error);
        }
    };

    // === 렌더링 컴포넌트 ===

    // 화면 1: 게임 메뉴
    if (gameState === 'MENU') {

        return (
            <div className="max-w-2xl mx-auto text-center space-y-8 animate-fade-in-up">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center justify-center gap-2">
                    <Gamepad2 className={`w-6 h-6 ${currentTheme.text}`}/>
                    한자 타자 게임
                </h2>

                <div className={`bg-white dark:bg-gray-800 p-10 rounded-3xl shadow-xl border-2 border-dashed ${currentTheme.border} space-y-8 transition-colors duration-300`}>
                    <div className={`w-24 h-24 ${currentTheme.bgLight} rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-slow transition-colors duration-300`}>
                        <Keyboard className={`w-12 h-12 ${currentTheme.text} transition-colors duration-300`} />
                    </div>

                    <div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">스피드 퀴즈 도전!</h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            표시되는 <b>한자</b>를 보고 해당하는 <b>히라가나</b>를 입력하세요.<br/>
                            예시: 前 → まえ<br/>
                            (비회원은 순위가 기록되지 않습니다)
                        </p>
                    </div>

                    <div className="space-y-4 max-w-xs mx-auto">
                        <div className="flex items-center gap-3 text-lg font-semibold text-gray-800 dark:text-gray-200">
                            <UserIcon className={`w-5 h-5 ${currentTheme.text}`} />
                            <span>Player: {user?.name || "Guest"}</span>
                        </div>

                        <div className="text-left">
                            {/*<label className="block text-sm font-bold text-gray-600 mb-1">난이도 선택</label>*/}
                            <div className="grid grid-cols-5 gap-2">
                                {[5, 4, 3, 2, 1].map((lv) => {
                                    // 각 버튼의 고유 테마 가져오기
                                    const btnTheme = getThemeColors(lv);
                                    const isActive = level === lv;

                                    return (
                                        <button
                                            key={lv}
                                            onClick={() => setLevel(lv)}
                                            className={`py-2 rounded-lg font-bold transition-all duration-200 ${
                                                isActive
                                                    ? `${btnTheme.bg} text-white shadow-md scale-105 ring-2 ring-offset-2 ${btnTheme.ring}`
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            N{lv}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={startGame}
                        disabled={loading}
                        className={`w-full max-w-xs px-8 py-4 ${currentTheme.bg} ${currentTheme.hoverBg} text-white font-bold rounded-xl shadow-lg ${currentTheme.shadow} transition-all active:scale-95 flex items-center justify-center gap-2 mx-auto`}
                    >
                        {loading ? <RefreshCw className="w-5 h-5 animate-spin"/> :
                            <Play className="w-5 h-5 fill-current"/>}
                        게임 시작
                    </button>
                </div>
            </div>
        );
    }

    // 화면 2: 게임 진행 중
    if (gameState === 'PLAYING') {
        const currentWord = words[currentIndex];

        return (
            <div className="max-w-xl mx-auto text-center space-y-6 animate-fade-in">
                {/* 상단 정보 */}
                <div className="flex justify-between items-center px-4">
                    <span className="font-bold text-gray-500 dark:text-gray-400">Level N{level}</span>
                    <span className="font-bold text-pink-600 dark:text-pink-400 text-lg">
                        {currentIndex + 1} / <span className="text-gray-300 dark:text-gray-600">{words.length}</span>
                    </span>
                </div>

                {/* 문제 카드 */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-3xl p-12 shadow-lg border-b-4 border-gray-100 dark:border-gray-900 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gray-100 dark:bg-gray-700">
                        <div
                            className="h-full bg-pink-500 transition-all duration-300"
                            style={{width: `${((currentIndex) / words.length) * 100}%`}}
                        />
                    </div>

                    <h2 className="text-6xl md:text-8xl font-black text-gray-800 dark:text-gray-100 mb-8 tracking-tighter">
                        {currentWord?.kanji}
                    </h2>

                    <form onSubmit={handleSubmit} className="relative max-w-sm mx-auto">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="히라가나 입력 (모르면 Enter)"
                            className="w-full text-center text-2xl font-bold py-4 border-b-2 border-gray-200 dark:border-gray-700 focus:border-pink-500 focus:outline-none bg-transparent dark:text-gray-100 placeholder:text-gray-300 dark:placeholder:text-gray-600 placeholder:text-lg placeholder:font-normal"
                            autoComplete="off"
                        />
                        <button
                            type="submit"
                            className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pink-500 p-2"
                        >
                            <CheckCircle2 className="w-6 h-6"/>
                        </button>
                    </form>
                    <p className="mt-4 text-gray-400 dark:text-gray-500 text-sm">
                        Enter를 누르면 다음 문제로 넘어갑니다.
                    </p>
                </div>
            </div>
        );
    }

    const username = user ? user.name : "Guest";
    // 화면 3: 결과 및 랭킹
    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-fade-in pb-10">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center justify-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-500"/>
                게임 결과
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
                {/* 1. 내 점수 & 오답노트 */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="bg-pink-500 text-white p-6 text-center">
                        <div className="text-sm font-medium opacity-80 mb-1">{username}님의 점수</div>
                        <div className="text-5xl font-black mb-2">{score}점</div>
                        <div className="flex justify-center items-center gap-2 text-sm opacity-90">
                            <Timer className="w-4 h-4"/>
                            {Math.floor((endTime - startTime) / 1000)}초 소요
                        </div>
                    </div>

                    <div className="p-6">
                        <h3 className="font-bold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-pink-500"/>
                            문제 다시보기
                        </h3>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                            {history.map((item, idx) => (
                                <div key={idx}
                                     className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-sm">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-15 h-8 flex items-center justify-center font-bold text-lg text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800 rounded shadow-sm">
                                            {item.word.kanji}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-600 dark:text-gray-300">{item.word.hiragana}</span>
                                            <span className="text-xs text-gray-400 dark:text-gray-500">{item.word.meaning}</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div
                                            className={`font-bold ${item.isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                                            {item.userAnswer || "(미입력)"}
                                        </div>
                                        {item.isCorrect ?
                                            <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto mt-1"/> :
                                            <XCircle className="w-4 h-4 text-red-500 ml-auto mt-1"/>
                                        }
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 2. 랭킹 리스트 */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
                    <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2 border-b dark:border-gray-700 pb-4">
                        <Trophy className="w-5 h-5 text-yellow-500"/>
                        N{level} 명예의 전당
                    </h3>

                    <div className="space-y-4">
                        {rankings.map((rank, idx) => (
                            <div key={idx} className={`flex items-center p-3 rounded-xl ${
                                rank.username === username && rank.score === score
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50' // 내 기록 하이라이트
                                    : 'bg-gray-50 dark:bg-gray-900/50'
                            }`}>
                                <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold mr-3 ${
                                    idx < 3 ? 'bg-yellow-500 text-white shadow-sm' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                }`}>
                                    {rank.rank}
                                </div>
                                <div className="flex-1">
                                    <div className="font-bold text-gray-800 dark:text-gray-100">{rank.username}</div>
                                    <div className="text-xs text-gray-400 dark:text-gray-500">{rank.elapsedTime}초</div>
                                </div>
                                <div className="font-black text-pink-600 dark:text-pink-400 text-lg">
                                    {rank.score}점
                                </div>
                            </div>
                        ))}
                        {rankings.length === 0 && (
                            <div className="text-center text-gray-400 dark:text-gray-500 py-10">
                                아직 등록된 랭킹이 없습니다.<br/>
                                1등에 도전해보세요!
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="text-center">
                <button
                    onClick={() => setGameState('MENU')}
                    className="px-8 py-3 bg-gray-800 dark:bg-gray-700 hover:bg-gray-900 dark:hover:bg-gray-600 text-white font-bold rounded-full shadow-lg transition-transform active:scale-95 flex items-center gap-2 mx-auto"
                >
                    <RefreshCw className="w-4 h-4"/>
                    다시 하기
                </button>
            </div>
        </div>
    );
};

export default TypingGame;